import { Component, inject, OnInit, signal } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../../core/utils";
import { FilterType } from "../../../constants/enums";
import { ParameterCatalog, SearchFields } from "../../../models";
import { DataTableService } from "../../../services";
import {
  FormDropdownComponent,
  FormDropdownMultiComponent,
  FormDropdownTreeComponent,
  FormInputComponent,
  FormInputDateRangeComponent,
  FormInputRangeComponent,
} from "../../form";
import { FormInputYearRangeComponent } from "../../form/form-input-year-range/form-input-year-range";

@Component({
  selector: "adaa-data-table-filters",
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormInputComponent,
    FormDropdownComponent,
    FormDropdownMultiComponent,
    FormInputDateRangeComponent,
    FormInputRangeComponent,
    FormDropdownTreeComponent,
    FormInputYearRangeComponent,
  ],
  templateUrl: "./data-table-filters.html",
  styleUrl: "./data-table-filters.scss",
})
export class DataTableFiltersComponent implements OnInit {
  // Set imperatively by the opener (`modal.componentInstance.<name>.set(...)`),
  // so these are writable signals rather than signal inputs.
  tableColumns = signal<ParameterCatalog[]>([]);
  filterValues = signal<SearchFields | undefined>(undefined);
  configKey = signal<string | undefined>(undefined);

  modal: NgbActiveModal = inject(NgbActiveModal);
  private _formBuilder: FormBuilder = inject(FormBuilder);
  private _dataTableService: DataTableService = inject(DataTableService);

  filterForm: FormGroup;
  adaaHelper = AdaaHelper;
  filterType = FilterType;
  readonly #untilDestroy = this.adaaHelper.untilDestroyed();

  public ngOnInit(): void {
    this._prepareForm();
  }

  public apply() {
    this.modal.close(this.filterForm.value);
  }

  public reset() {
    this.modal.close(true);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public dropdownChanged(event: any, item: ParameterCatalog): void {
    //If the dropdown don't have effect return
    if (this._dataTableService.filtersHaveEffect.findIndex((e) => e === item.fieldName) === -1) return;

    this._dataTableService
      .updateTableColumns(event, { item, configKey: this.configKey() as string }, this.tableColumns())
      .pipe(this.#untilDestroy())
      .subscribe((response) => this.tableColumns.set(response));
  }

  private _prepareForm() {
    this.filterForm = this._formBuilder.group({});
    if (this.tableColumns()) {
      this.tableColumns.update((columns) => columns.filter((e) => e.filterType !== this.filterType.ignored));
      this.tableColumns().forEach((param: ParameterCatalog) => {
        this.filterForm.addControl(param.fieldName, new FormControl(null, []));
      });
    }

    this._setFormValue();
  }

  private _setFormValue() {
    if (this.filterValues()) {
      this.tableColumns().forEach((e) => {
        if (this.filterValues()!.searchFields[e.dbFieldName]) {
          this.filterForm.get(e.fieldName)?.setValue(this._getFilterValue(e.filterType, e.dbFieldName));
        }
      });
    }
  }

  private _getFilterValue(filterType: FilterType, dbFieldName: string): string | object[] | undefined {
    const searchFields = this.filterValues()!.searchFields;

    if (filterType === FilterType.dropdownMulti) {
      return searchFields[dbFieldName]?.lov as object[];
    } else if (
      filterType === FilterType.dateRange ||
      filterType === FilterType.valueRange ||
      filterType === FilterType.YearRange
    ) {
      let date = "";
      if (searchFields[dbFieldName]?.range) {
        if (searchFields[dbFieldName]?.range?.startNumeric != null)
          date += `${searchFields[dbFieldName]?.range?.startNumeric} - `;

        if (searchFields[dbFieldName]?.range?.endNumeric != null)
          date += `${searchFields[dbFieldName]?.range?.endNumeric}`;
      }
      return date;
    } else {
      return searchFields[dbFieldName]?.value as never;
    }
  }
}
